import { useState, useEffect, useCallback } from 'react'
import { Search, BookOpen, ChevronDown, ChevronUp, Loader2, Copy, Check, ExternalLink, ArrowLeft } from 'lucide-react'

interface Surah {
  number: number
  name: string
  englishName: string
  englishNameTranslation: string
  revelationType: string
  numberOfAyahs: number
}

interface Ayah {
  number: number
  text: string
  numberInSurah: number
  juz: number
  manzil: number
  page: number
  ruku: number
  hizbQuarter: number
  sajda: boolean
}

interface SurahDetail {
  number: number
  name: string
  englishName: string
  englishNameTranslation: string
  revelationType: string
  numberOfAyahs: number
  ayahs: Ayah[]
}

const QURAN_API = 'https://api.alquran.cloud/v1'
const CACHE_KEY = 'islam-preacher-surahs'

export function ScriptureSearch() {
  const [surahs, setSurahs] = useState<Surah[]>([])
  const [search, setSearch] = useState('')
  const [selectedSurah, setSelectedSurah] = useState<SurahDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingSurah, setLoadingSurah] = useState(false)
  const [copiedVerse, setCopiedVerse] = useState<number | null>(null)

  useEffect(() => {
    const cached = localStorage.getItem(CACHE_KEY)
    if (cached) {
      setSurahs(JSON.parse(cached))
      setLoading(false)
      return
    }

    fetch(`${QURAN_API}/surah`)
      .then(r => r.json())
      .then(d => {
        if (d.code === 200) {
          setSurahs(d.data)
          localStorage.setItem(CACHE_KEY, JSON.stringify(d.data))
        }
      })
      .finally(() => setLoading(false))
  }, [])

  const loadSurah = useCallback(async (number: number) => {
    setLoadingSurah(true)
    setSelectedSurah(null)
    try {
      const resp = await fetch(`${QURAN_API}/surah/${number}/editions/en.asad,en.pickthall,en.yusufali`)
      const data = await resp.json()
      if (data.code === 200) {
        const main = data.data[0]
        const ayahs = main.ayahs.map((a: any, i: number) => ({
          number: a.number,
          text: a.text,
          numberInSurah: a.numberInSurah,
          juz: a.juz,
          manzil: a.manzil,
          page: a.page,
          ruku: a.ruku,
          hizbQuarter: a.hizbQuarter,
          sajda: a.sajda,
        }))

        setSelectedSurah({ ...main, ayahs })
      }
    } finally {
      setLoadingSurah(false)
    }
  }, [])

  const copyVerse = async (text: string, verseNum: number) => {
    await navigator.clipboard.writeText(text)
    setCopiedVerse(verseNum)
    setTimeout(() => setCopiedVerse(null), 2000)
  }

  const filtered = surahs.filter(s =>
    s.englishName.toLowerCase().includes(search.toLowerCase()) ||
    s.name.includes(search) ||
    s.englishNameTranslation?.toLowerCase().includes(search.toLowerCase()) ||
    String(s.number).includes(search)
  )

  if (selectedSurah) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container py-6">
          <button
            onClick={() => setSelectedSurah(null)}
            className="flex items-center gap-2 text-small text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--accent))] mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to surah list
          </button>

          <div className="mb-6">
            <h1 className="text-h2 text-[hsl(var(--fg))]">
              {selectedSurah.englishName} <span className="text-[hsl(var(--accent))]">({selectedSurah.name})</span>
            </h1>
            <p className="text-small text-[hsl(var(--fg-muted))]">
              {selectedSurah.englishNameTranslation} — {selectedSurah.revelationType} · {selectedSurah.numberOfAyahs} verses
            </p>
          </div>

          {loadingSurah ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>
          ) : (
            <div className="stack gap-4">
              {selectedSurah.ayahs.map(ayah => (
                <div
                  key={ayah.number}
                  id={`verse-${ayah.numberInSurah}`}
                  className="card hover:border-[hsl(var(--accent)/0.2)] transition-all"
                >
                  <div className="row-between mb-2">
                    <span className="badge">{ayah.numberInSurah}</span>
                    <button
                      onClick={() => copyVerse(ayah.text, ayah.numberInSurah)}
                      className="btn btn-ghost btn-icon opacity-0 group-hover:opacity-100 transition-all"
                      title="Copy verse"
                    >
                      {copiedVerse === ayah.numberInSurah ? <Check className="h-4 w-4 text-[hsl(var(--success))]" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-right text-h3 leading-loose mb-3 font-arabic text-[hsl(var(--fg))]" dir="rtl">
                    {ayah.text}
                  </p>
                  <div className="divider" />
                  <div className="text-caption text-[hsl(var(--fg-muted))] space-y-0.5">
                    <p>Juz {ayah.juz} · Page {ayah.page} · Ruku {ayah.ruku}</p>
                    {ayah.sajda && <p className="text-[hsl(var(--accent))]">— Sajda (prostration verse)</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="container py-6">
        <div className="mb-6">
          <h1 className="text-h2 text-[hsl(var(--fg))] mb-1">Scripture Browser</h1>
          <p className="text-small text-[hsl(var(--fg-muted))]">
            Browse and search the Quran. Click any surah to read full text with translations.
          </p>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--fg-muted))]" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search surahs by name or number..."
            className="input-field pl-11"
            autoFocus
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>
        ) : (
          <div className="grid-3 gap-2">
            {filtered.map(surah => (
              <button
                key={surah.number}
                onClick={() => loadSurah(surah.number)}
                className="group card row gap-4 p-4 text-left transition-all hover:border-[hsl(var(--accent)/0.4)] hover:bg-[hsl(var(--accent-soft))] active:scale-[0.98]"
              >
                <div className="badge flex h-10 w-10 shrink-0 items-center justify-center text-small font-bold">
                  {surah.number}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="row gap-2">
                    <span className="text-small font-medium text-[hsl(var(--fg))] truncate">{surah.englishName}</span>
                    <span className="text-caption text-[hsl(var(--fg-muted))] opacity-60">{surah.name}</span>
                  </div>
                  <p className="text-caption text-[hsl(var(--fg-muted))] truncate">
                    {surah.englishNameTranslation} · {surah.numberOfAyahs} verses
                  </p>
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 text-[hsl(var(--fg-muted))] -rotate-90 group-hover:text-[hsl(var(--accent))] transition-colors" />
              </button>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <p className="py-20 text-center text-small text-[hsl(var(--fg-muted))]">
            No surahs found matching &ldquo;{search}&rdquo;
          </p>
        )}

        <p className="mt-8 text-center text-caption text-[hsl(var(--fg-muted))]">
          Data sourced from <a href="https://alquran.cloud" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--accent))] underline">Al-Quran Cloud</a>
        </p>
      </div>
    </div>
  )
}
